import React, { useContext, useEffect, useState } from 'react';

// Third-party packages.
import * as dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import * as objectSupport from 'dayjs/plugin/objectSupport';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Helper.
import Helper from '../helpers/Helper';
import { determineTimeUnit } from "../helpers/utils";
import { resolveUserTimezone, normalizeTz, dateForPickerDisplay, dateFromPickerSelection } from "../helpers/timeRangeUtils";

// Constants.
import {
    INTERVAL_10_SECONDS,
    INTERVAL_15_SECONDS,
    INTERVAL_5_SECONDS,
    INTERVAL_FIFTEEN_MINUTES,
    INTERVAL_FIVE_MINUTES,
    INTERVAL_MINUTE,
    INTERVAL_ONE_DAY,
    INTERVAL_ONE_HOUR,
    INTERVAL_ONE_WEEK,
    INTERVAL_SECOND,
    INTERVAL_SIX_HOURS,
    PERIOD_CUSTOM,
    PERIOD_DAY_BEFORE_YESTERDAY,
    PERIOD_FIVE_MINUTES,
    PERIOD_MINUTE,
    PERIOD_ONE_HOUR,
    PERIOD_PREVIOUS_WEEK,
    PERIOD_SEVEN_DAYS,
    PERIOD_SIX_HOURS,
    PERIOD_TEN_MINUTES,
    PERIOD_THIRTY_MINUTES,
    PERIOD_THIRTY_ONE_DAYS,
    PERIOD_THIS_WEEK,
    PERIOD_TODAY,
    PERIOD_TWELVE_HOURS,
    PERIOD_TWENTY_FOUR_HOURS,
    PERIOD_YESTERDAY,
    periodToValidIntervals
} from './Constants';
import { TimeRangeContext } from "../context/TimeRangeContext";
import { useLocation } from 'react-router-dom';
import ConditionalRender from "../helpers/ConditionalRender";
import { Dropdown } from "../components/forms";

const AGGREGATE_OPTIONS = [
    { value: INTERVAL_SECOND, label: '1 second' },
    { value: INTERVAL_5_SECONDS, label: '5 seconds' },
    { value: INTERVAL_10_SECONDS, label: '10 seconds' },
    { value: INTERVAL_15_SECONDS, label: '15 seconds' },
    { value: INTERVAL_MINUTE, label: '1 Minute' },
    { value: INTERVAL_FIVE_MINUTES, label: '5 Minutes' },
    { value: INTERVAL_FIFTEEN_MINUTES, label: '15 Minutes' },
    { value: INTERVAL_ONE_HOUR, label: '1 hour' },
    { value: INTERVAL_SIX_HOURS, label: '6 hours' },
    { value: INTERVAL_ONE_DAY, label: '1 day' },
    { value: INTERVAL_ONE_WEEK, label: '1 week' }
];

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(objectSupport.default);

function PeriodSelector() {
    const timeRangeContext = useContext(TimeRangeContext)

    const [periodOption, setPeriodOption] = useState<number>(timeRangeContext.timeRange.period);
    const [fmDate, setFmDate] = useState<Dayjs>(timeRangeContext.timeRange.from);
    const [toDate, setToDate] = useState<Dayjs>(timeRangeContext.timeRange.to);
    const [inputClass, setInputClass] = useState<string>('');
    const [selectedTimezone, setSelectedTimezone] = useState<string>(resolveUserTimezone)

    const [toTime, setToTime] = useState<string>(timeRangeContext.timeRange.to.format('HH:mm'));
    const [fmTime, setFmTime] = useState<string>(timeRangeContext.timeRange.from.format('HH:mm'));
    const [headingText, setHeadingText] = useState<string>('');
    const [subHeadingText, setSubHeadingText] = useState<string>('');
    const [isAutoRefreshActive, setIsAutoRefreshActive] = useState(false);
    const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState<NodeJS.Timeout | null>(null);

    const agg = JSON.parse(window.localStorage.getItem('dbmar-interval') || '0')
    const aggValid = Helper.aggregateIsValid(agg, timeRangeContext)
    const [aggregate, setAggregate] = useState<number>(aggValid ? agg : 0);
    const location = useLocation();

    const getHeadingsText = (periodValue: number, from: Dayjs, to: Dayjs) => {
        let heading: string = 'Custom';
        let subHeading: string = timeRangeContext.timeRange.from.format('Do MMM')

        // Set the heading.
        switch (periodValue) {
            case PERIOD_CUSTOM:
                heading = 'Custom';
                break;
            case PERIOD_MINUTE:
                heading = 'Last minute';
                break;
            case PERIOD_FIVE_MINUTES:
                heading = 'Last 5 minutes';
                break;
            case PERIOD_TEN_MINUTES:
                heading = 'Last 10 minutes';
                break;
            case PERIOD_THIRTY_MINUTES:
                heading = 'Last 30 minutes';
                break;
            case PERIOD_ONE_HOUR:
                heading = 'Last hour';
                break;
            case PERIOD_SIX_HOURS:
                heading = 'Last 6 hours';
                break;
            case PERIOD_TWELVE_HOURS:
                heading = 'Last 12 hours';
                break;
            case PERIOD_TWENTY_FOUR_HOURS:
                heading = 'Last 24 hours';
                break;
            case PERIOD_SEVEN_DAYS:
                heading = '7 days';
                break;
            case PERIOD_THIRTY_ONE_DAYS:
                heading = '31 days';
                break;
            case PERIOD_TODAY:
                heading = 'Today';
                break;
            case PERIOD_YESTERDAY:
                heading = 'Yesterday';
                break;
            case PERIOD_DAY_BEFORE_YESTERDAY:
                heading = 'Day Before Yesterday';
                break;
            case PERIOD_THIS_WEEK:
                heading = 'This Week';
                break;
            case PERIOD_PREVIOUS_WEEK:
                heading = 'Previous Week';
                break;
            default:
                break;
        }

        // Set the subheading.
        if (from.diff(to, 'day') !== 0) {
            // The selected period greater than one day.
            subHeading = Helper.getFormattedPeriodSubheading(from, to)
        } else {
            // The selected period is a same day enquiry.
            subHeading = from.format('Do MMM');
        }

        setHeadingText(heading)
        setSubHeadingText(subHeading)
    }

    // Compute the validity class for a from/to range. Returns 'is-invalid' when
    // the times are malformed, unparseable, or the range is not strictly
    // increasing; '' when the range is valid. Kept as the single source of
    // truth so every input change (time blur, date pick, Apply) stays in sync.
    function getInvalidClass(fromDate: Dayjs, fromTime: string, toDate: Dayjs, toTime: string): string {
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(:[0-5][0-9])?$/;
        if (!timeRegex.test(fromTime) || !timeRegex.test(toTime)) {
            return 'is-invalid';
        }

        const from = buildTime(fromDate, fromTime),
                to = buildTime(toDate, toTime);

        if (!from.isValid() || !to.isValid()) {
            return 'is-invalid';
        }

        if (from.valueOf() >= to.valueOf()) {
            return 'is-invalid';
        }

        return '';
    }

    function validatePeriod() {
        setInputClass(getInvalidClass(fmDate, fmTime, toDate, toTime));
    }

    function clearStyle(id: string): void {
        const element = document.getElementById(id);

        if (element) {
            element.classList.remove('show');
            element.setAttribute('aria-expanded', 'false');
        }
    }

    function closePeriodDropdown() {
        clearStyle('period');
        clearStyle('periodDropdown');
    }

    function updatePeriod(option: number, timeZone?: string) {
        // The period selector opens by toggling CSS. It does not close when
        // one of the predefined values are chosen. Close using JavaScript.
        // In contrast, React requires the parent view to be temporarily
        // different, while it is loading, in order to force the period
        // selector to be recreated in the closed state. Every view would need
        // to do this. It also appears quite jarring.
        closePeriodDropdown();
        
        // Stop auto-refresh when period is changed
        clearAutoRefresh();
        
        const currentTimeZone = timeZone || selectedTimezone

        let to = dayjs.default(),
                from = to;

        // Set the from and to state values.
        switch (option) {
            case PERIOD_MINUTE:
                from = to.subtract(1, 'minute');
                break;
            case PERIOD_FIVE_MINUTES:
                from = to.subtract(5, 'minute');
                break;
            case PERIOD_TEN_MINUTES:
                from = to.subtract(10, 'minute');
                break;
            case PERIOD_THIRTY_MINUTES:
                from = to.subtract(30, 'minute');
                break;
            case PERIOD_ONE_HOUR:
                to = to.second(0)
                from = to.subtract(1, 'hour')
                break;
            case PERIOD_SIX_HOURS:
                to = to.second(0)
                from = to.subtract(6, 'hour')
                break;
            case PERIOD_TWELVE_HOURS:
                to = to.second(0);
                from = to.subtract(12, 'hour')
                break;
            case PERIOD_TWENTY_FOUR_HOURS:
                to = to.second(0);
                from = to.subtract(24, 'hour')
                break;
            case PERIOD_SEVEN_DAYS:
                from = to.subtract(7, 'day');
                break;
            case PERIOD_THIRTY_ONE_DAYS:
                from = to.subtract(31, 'day');
                break;
            case PERIOD_TODAY:
                from = to.startOf('day');
                to = from.add(1, 'day').startOf('day');
                break;
            case PERIOD_YESTERDAY:
                from = to.subtract(1, 'day').startOf('day');
                to = from.add(1, 'day').startOf('day');
                break;
            case PERIOD_DAY_BEFORE_YESTERDAY:
                from = to.subtract(2, 'day').startOf('day');
                to = from.add(1, 'day').startOf('day');
                break;
            case PERIOD_THIS_WEEK:
                from = to.startOf('week');
                to = from.add(1, 'week');
                break;
            case PERIOD_PREVIOUS_WEEK:
                from = to.subtract(1, 'week').startOf('week');
                to = from.add(1, 'week');
                break;
            default:
                break;
        }

        const format: string = ((option === PERIOD_MINUTE || option === PERIOD_FIVE_MINUTES) ? 'HH:mm:ss' : 'HH:mm');

        const tzFrom = currentTimeZone === 'UTC' ? from.utc() : from.tz(currentTimeZone)
        const tzTo = currentTimeZone === 'UTC' ? to.utc() : to.tz(currentTimeZone)

        // Set the selected period option and defined period.
        setPeriodOption(option);
        setFmDate(tzFrom);
        setFmTime(tzFrom.format(format));
        setToDate(tzTo);
        setToTime(tzTo.format(format));

        const presetAggregate = intervalDisabledFor(aggregate, to.diff(from, 'milliseconds')) ? 0 : aggregate
        if (presetAggregate !== aggregate) {
            setAggregate(presetAggregate)
        }
        window.localStorage.setItem('dbmar-interval', JSON.stringify(presetAggregate))
        let updatedFrom = tzFrom
        let updatedTo = tzTo

        // don't apply timezone if is a day or more days
        if (option === PERIOD_TODAY || option === PERIOD_YESTERDAY || option === PERIOD_DAY_BEFORE_YESTERDAY || option === PERIOD_THIS_WEEK || option === PERIOD_PREVIOUS_WEEK) {
            updatedFrom = from
            updatedTo = to
        }

        // DBMAR-276 - auto apply pre-defined time period.
        timeRangeContext.applyPeriod(option, updatedFrom, updatedTo, currentTimeZone);
    }

    const updateAggregateOnPeriodChange = () => {
        // Stop auto-refresh when user starts making manual changes
        clearAutoRefresh();
    }

    function setPeriod() {

        // Validate the entered time values.
        const invalidClass = getInvalidClass(fmDate, fmTime, toDate, toTime);
        setInputClass(invalidClass);
        if (invalidClass) {
            return;
        }

        const from = buildTime(fmDate, fmTime),
                to = buildTime(toDate, toTime);

        // Stop auto-refresh when period is changed
        clearAutoRefresh();

        const appliedAggregate = aggregateDisabled(aggregate) ? 0 : aggregate
        if (appliedAggregate !== aggregate) {
            setAggregate(appliedAggregate)
        }
        window.localStorage.setItem('dbmar-interval', JSON.stringify(appliedAggregate))

        // Close the dropdown after applying
        closePeriodDropdown();
        timeRangeContext.applyPeriod(periodOption, from, to, selectedTimezone);
    }

    const applyTimeZone = (value: string) => {
        const normalized = normalizeTz(value)
        window.localStorage.setItem('dbmar-timeZone', JSON.stringify(normalized))

        // Relative presets recompute against "now" in the new timezone, keeping
        // the user's chosen preset rather than forcing it back to "Last hour".
        if (periodOption !== PERIOD_CUSTOM) {
            setSelectedTimezone(normalized)
            updatePeriod(periodOption, normalized)
            return
        }

        // Custom range: keep the same clock numbers the user is looking at and
        // reinterpret them in the newly selected timezone (the instant shifts).
        const oldTz = selectedTimezone
        const fromWallDate = (oldTz === 'UTC' ? fmDate.utc() : fmDate.tz(oldTz)).format('YYYY-MM-DD')
        const toWallDate = (oldTz === 'UTC' ? toDate.utc() : toDate.tz(oldTz)).format('YYYY-MM-DD')
        const buildInTz = (dateStr: string, time: string) => {
            const str = `${dateStr}T${time}`
            return normalized === 'UTC' ? dayjs.utc(str) : dayjs.tz(str, normalized)
        }
        const newFrom = buildInTz(fromWallDate, fmTime)
        const newTo = buildInTz(toWallDate, toTime)

        setSelectedTimezone(normalized)
        setFmDate(newFrom)
        setToDate(newTo)
        timeRangeContext.applyPeriod(PERIOD_CUSTOM, newFrom, newTo, normalized)
    }

    function buildTime(date: Dayjs, time: string) {
        // `date` is an instant; its calendar date is read as seen in the selected
        // timezone so the picker display, this built value, and validation all
        // agree regardless of the browser's own timezone.
        const viewed = selectedTimezone === 'UTC' ? date.utc() : date.tz(selectedTimezone);
        const dateStr = viewed.format('YYYY-MM-DD') + 'T' + time;
        if (selectedTimezone === 'UTC') {
            return dayjs.utc(dateStr);
        }
        return dayjs.tz(dateStr, selectedTimezone);
    }


    // A minute: INTERVAL_SECOND, INTERVAL_5_SECONDS, INTERVAL_15_SECONDS
    // More than a minute less(or equal) than 15 minutes: INTERVAL_SECOND, INTERVAL_5_SECONDS, INTERVAL_15_SECONDS, INTERVAL_MINUTE
    // More than 15 minutes less(or equal) than an hour: INTERVAL_MINUTE, INTERVAL_FIVE_MINUTES, INTERVAL_FIFTEEN_MINUTES
    // More than an hour less(or equal) than a day: INTERVAL_MINUTE, INTERVAL_FIVE_MINUTES, INTERVAL_FIFTEEN_MINUTES, INTERVAL_ONE_HOUR
    // More than a day less(or equal) than a week: INTERVAL_ONE_HOUR, INTERVAL_SIX_HOURS, INTERVAL_ONE_DAY
    // More than a week: INTERVAL_SIX_HOURS, INTERVAL_ONE_DAY, INTERVAL_ONE_WEEK

    // Sensor status is sampled per minute, so that page allows no sub-minute
    // intervals ("Auto" stays available), and its history query is too heavy
    // for the "Last week" period.
    const isSensorStatusPage = location.pathname.startsWith('/sensor-status');

    // Week-plus presets pull too much sensor-status history in one query.
    const periodDisabled = (period: number) =>
        isSensorStatusPage && [PERIOD_SEVEN_DAYS, PERIOD_THIRTY_ONE_DAYS, PERIOD_THIS_WEEK, PERIOD_PREVIOUS_WEEK].includes(period);

    const pendingRangeMs = getInvalidClass(fmDate, fmTime, toDate, toTime) === ''
        ? buildTime(toDate, toTime).diff(buildTime(fmDate, fmTime), 'milliseconds')
        : timeRangeContext.timeRange.to.diff(timeRangeContext.timeRange.from, 'milliseconds');

    const intervalDisabledFor = (aggregateValue: number, rangeMs: number) => {
        if (isSensorStatusPage && aggregateValue !== 0 && aggregateValue < INTERVAL_MINUTE) {
            return true;
        }
        const getTimePeriod = determineTimeUnit(rangeMs)
        return !periodToValidIntervals[getTimePeriod].includes(aggregateValue)
    }

    const aggregateDisabled = (aggregateValue: number) => intervalDisabledFor(aggregateValue, pendingRangeMs)

    useEffect(() => {
        if (aggregate !== 0 && aggregateDisabled(aggregate)) {
            window.localStorage.setItem('dbmar-interval', '0')
            setAggregate(0)
        }
    }, [pendingRangeMs, isSensorStatusPage]);

    // Keep the local period option in sync with the context.
    useEffect(() => {
        setPeriodOption(timeRangeContext.timeRange.period);
    }, [timeRangeContext.timeRange.period]);

    useEffect(() => {
        setSelectedTimezone(normalizeTz(timeRangeContext.timeRange.timezone));
    }, [timeRangeContext.timeRange.timezone]);


    // Sync the editable time fields, aggregate and heading text whenever the
    // context time range or period changes. Uses the context period directly
    // for the heading (periodOption tracks it via the effect above).
    useEffect(() => {
        setFmDate(timeRangeContext.timeRange.from)
        setToDate(timeRangeContext.timeRange.to)
        setToTime(timeRangeContext.timeRange.to.format('HH:mm'))
        setFmTime(timeRangeContext.timeRange.from.format('HH:mm'))
        // Use the period from the context directly for text calculation
        getHeadingsText(timeRangeContext.timeRange.period, timeRangeContext.timeRange.from, timeRangeContext.timeRange.to)
        // Depend on the primitives that this effect actually reads.
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.period, timeRangeContext.timeRange.timezone])


    const setDateValue = (date: Date, type: string) => {
        const validDate = date || new Date()
        // The picker hands back a wall-clock date in its own display space;
        // interpret it in the selected timezone so the stored instant round-trips
        // with `dateForPickerDisplay` and buildTime (see the picker `selected` props).
        const instant = dateFromPickerSelection(validDate, selectedTimezone) || validDate
        const newDate = dayjs.default(instant)
        // Re-validate against the new date so a previously-set 'is-invalid' is
        // cleared once the range becomes valid again (and vice versa). Picking a
        // date must keep the validity flag in sync, not leave it stale.
        if (type === 'to') {
            setToDate(newDate)
            setInputClass(getInvalidClass(fmDate, fmTime, newDate, toTime))
        } else {
            setFmDate(newDate)
            setInputClass(getInvalidClass(newDate, fmTime, toDate, toTime))
        }
        updateAggregateOnPeriodChange()
    }

    // Nudge the current time range backward or forward by 50% of its duration
    const nudgePeriod = (direction: 'back' | 'forward') => {
        const { from, to } = timeRangeContext.timeRange;
        if (!from?.isValid() || !to?.isValid()) {
            return;
        }
        const diffMs = to.diff(from, 'millisecond');
        if (diffMs <= 0) {
            return;
        }
        const halfMs = Math.floor(diffMs / 2);
        const sign = direction === 'back' ? -1 : 1;
        const newFrom = from.add(sign * halfMs, 'millisecond');
        const newTo = to.add(sign * halfMs, 'millisecond');

        // Stop auto-refresh when period is nudged
        clearAutoRefresh();


        // Treat as a custom range; preserve current aggregate (do not reset here)
        timeRangeContext.applyPeriod(PERIOD_CUSTOM, newFrom, newTo, selectedTimezone);
    }

    // Build dynamic nudge amount label based on current range (prefer mins/hours, fallback to seconds if < 1 min)
    const getNudgeAmountLabel = (): string => {
        const { from, to } = timeRangeContext.timeRange;
        if (!from || !to) return '';
        const diffMs = to.diff(from, 'millisecond');
        if (diffMs <= 0) return '';
        const halfMs = diffMs / 2;

        const ONE_HOUR = 60 * 60 * 1000;
        const ONE_MINUTE = 60 * 1000;

        if (halfMs >= ONE_HOUR) {
            const hours = halfMs / ONE_HOUR;
            const rounded = Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(1);
            return `${rounded} ${rounded === '1' ? 'hour' : 'hours'}`;
        } else if (halfMs >= ONE_MINUTE) {
            const mins = halfMs / ONE_MINUTE;
            const rounded = Number.isInteger(mins) ? mins.toFixed(0) : mins.toFixed(1);
            return `${rounded} ${rounded === '1' ? 'min' : 'mins'}`;
        } else {
            const secs = Math.max(1, Math.round(halfMs / 1000));
            return `${secs}s`;
        }
    };

    const getNudgeTooltip = (direction: 'back' | 'forward') =>
            `${direction === 'back' ? 'Nudge back' : 'Nudge forward'} by ${getNudgeAmountLabel()}`;

    const isRefreshablePeriod = [PERIOD_MINUTE, PERIOD_FIVE_MINUTES, PERIOD_TEN_MINUTES, PERIOD_THIRTY_MINUTES, PERIOD_ONE_HOUR].includes(periodOption);


    // Helper function for auto-refresh updates (calls applyPeriod directly with autorefresh=true)
    const autoRefreshUpdate = () => {
        const currentTimeZone = selectedTimezone;
        let to = dayjs.default(),
            from = to;

        // Set the from and to state values for the current period
        switch (periodOption) {
            case PERIOD_MINUTE:
                from = to.subtract(1, 'minute');
                break;
            case PERIOD_FIVE_MINUTES:
                from = to.subtract(5, 'minute');
                break;
            case PERIOD_TEN_MINUTES:
                from = to.subtract(10, 'minute');
                break;
            case PERIOD_THIRTY_MINUTES:
                from = to.subtract(30, 'minute');
                break;
            case PERIOD_ONE_HOUR:
                from = to.subtract(1, 'hour');
                break;
            default:
                from = to.subtract(1, 'hour');
                break;
        }

        let updatedFrom = currentTimeZone === 'UTC' ? from.utc() : from.tz(currentTimeZone);
        let updatedTo = currentTimeZone === 'UTC' ? to.utc() : to.tz(currentTimeZone);

        // Call applyPeriod directly with autorefresh=true
        timeRangeContext.applyPeriod(periodOption, updatedFrom, updatedTo, currentTimeZone, true);
    };

    // Helper function to clear autorefresh
    const clearAutoRefresh = () => {
        if (autoRefreshIntervalId) {
            clearInterval(autoRefreshIntervalId);
            setAutoRefreshIntervalId(null);
            setIsAutoRefreshActive(false);
            
            // Update timeRange context to set autorefresh: false
            const currentTimeZone = selectedTimezone;
            const from = fmDate;
            const to = toDate;
            timeRangeContext.applyPeriod(periodOption, from, to, currentTimeZone, false);
        }
    };

    const toggleAutoRefresh = () => {
        if (autoRefreshIntervalId) {
            clearAutoRefresh();
        } else {
            const newIntervalId = setInterval(() => {
                autoRefreshUpdate();
            }, aggregate || 10000); // Use selected interval or default to 10 seconds
            setAutoRefreshIntervalId(newIntervalId);
            setIsAutoRefreshActive(true);
        }
    };

    useEffect(() => {
        // Clear interval if the selected period is no longer refreshable
        if (!isRefreshablePeriod) {
            clearAutoRefresh();
        }

        // Cleanup interval on component unmount
        return () => {
            if (autoRefreshIntervalId) {
                clearInterval(autoRefreshIntervalId);
            }
        };
    }, [periodOption, autoRefreshIntervalId, isRefreshablePeriod]);

    // Restart auto-refresh when interval changes
    useEffect(() => {
        if (isAutoRefreshActive && autoRefreshIntervalId) {
            clearInterval(autoRefreshIntervalId);
            const newIntervalId = setInterval(() => {
                autoRefreshUpdate();
            }, aggregate || 10000);
            setAutoRefreshIntervalId(newIntervalId);
        }
    }, [aggregate]);

    // Reset auto-refresh on navigation
    useEffect(() => {
        clearAutoRefresh();
    }, [location.pathname]);

    const playPauseButtonStyle = isAutoRefreshActive ? {
        backgroundColor: '#28a745',
        borderColor: '#28a745',
        color: '#fff'
    } : {};

    return (
            <React.Fragment>
                {/*  data-bs-offset="0,20" */}
                <button type="button" className="btn btn-heading btn-nudge btn-nudge-left" aria-label="Previous range" data-tooltip-id="global-tooltip" data-tooltip-content={getNudgeTooltip('back')}
                        onClick={() => nudgePeriod('back')}>
                    <i className="fal fa-chevron-left" aria-hidden="true"/>
                </button>
                <button id="period" type="button" className="btn btn btn-dropdown dropdown-toggle btn-heading"
                        data-bs-toggle="dropdown" aria-expanded="false" data-class="text-center"
                        data-tooltip-id="global-tooltip" data-tooltip-content={Helper.getFormattedPeriodHeadingFromDate(timeRangeContext.timeRange.from, timeRangeContext.timeRange.to)}>
                    <i className="fal fa-clock fa-fw" aria-hidden="true"/> {/* Use <i> for icon for consistency */}
                    <div className="period">
                        <span className="period-heading">{headingText}</span> {/* Wrap heading */}
                        <div className="period-subheading">{subHeadingText}</div> {/* Add class to subheading div */}
                    </div>
                </button>
                <button type="button" className="btn btn-heading btn-nudge btn-nudge-right" aria-label="Next range" data-tooltip-id="global-tooltip" data-tooltip-content={getNudgeTooltip('forward')}
                        onClick={() => nudgePeriod('forward')}>
                    <i className="fal fa-chevron-right" aria-hidden="true"/>
                </button>
                <button type="button" className="btn btn-heading btn-nudge btn-play-pause"
                        aria-label={isAutoRefreshActive ? 'Pause auto-refresh' : 'Start auto-refresh'}
                        data-tooltip-id="global-tooltip" data-tooltip-content={isAutoRefreshActive ? 'Pause auto-refresh' : 'Start auto-refresh'}
                        onClick={toggleAutoRefresh}
                        disabled={!isRefreshablePeriod}
                        style={playPauseButtonStyle}>
                    <i className={`fal ${isAutoRefreshActive ? 'fa-pause' : 'fa-play'}`} aria-hidden="true"/>
                </button>
                <div id="periodDropdown" className="dropdown-menu period-dropdown-menu" aria-labelledby="period">
                    <div className="row g-0 heading">
                        <div className="col-xs-12 col-lg-5">
                            <i className="fal fa-clock fa-fw" aria-hidden="true"/>
                            Custom Time Range
                        </div>
                        <div className="col-xs-12 col-lg-7 d-none d-lg-block">
                            <i className="fal fa-history fa-fw" aria-hidden="true"/>
                            Time Presets
                        </div>
                    </div>
                    <div className="row g-0" onClick={(e) => e.stopPropagation()}>
                        {/* Custom Period */}
                        <div className="col-xs-12 col-lg-5 border-lg-end pt-2">
                            <div className="row row-cols-1 g-0">
                                <div className="col px-3 px-md-2">
                                    <div className="row g-0">
                                        <div className="col subheading">
                                            From
                                        </div>
                                        <div className="w-100"/>
                                        <div className="col">
                                            <label htmlFor="fmDate">Date</label>
                                        </div>
                                        <div className="col">
                                            {fmDate.isValid() && toDate.isValid() && (
                                                    <DatePicker
                                                            id="fmDate"
                                                            className={`form-control form-control-sm ${inputClass}`}
                                                            dateFormat="yyyy/MM/dd"
                                                            placeholderText="YYYY/MM/DD"
                                                            maxDate={dateForPickerDisplay(toDate.toDate(), selectedTimezone) || undefined}
                                                            popperPlacement="top-end"
                                                            selected={dateForPickerDisplay(fmDate.toDate(), selectedTimezone)}
                                                            onCalendarClose={() => setPeriodOption(PERIOD_CUSTOM)}
                                                            onChange={(date: Date) => setDateValue(date, 'from')}
                                                            shouldCloseOnSelect={true}/>
                                            )}
                                        </div>
                                        <div className="w-100 py-1"/>
                                        <div className="col">
                                            <label htmlFor="fmTime">Time</label>
                                        </div>
                                        <div className="col">
                                            <input type="text"
                                                   id="fmTime"
                                                   className={`form-control form-control-sm ${inputClass}`}
                                                   maxLength={5}
                                                   placeholder="HH:MM"
                                                   value={fmTime}
                                                   onBlur={validatePeriod}
                                                   onChange={(e) => {
                                                       updateAggregateOnPeriodChange()
                                                       setFmTime(e.target.value);
                                                       setPeriodOption(PERIOD_CUSTOM)
                                                   }
                                                   }/>
                                        </div>
                                        <div className="w-100 py-1"/>
                                        <div className="col subheading">
                                            To
                                        </div>
                                        <div className="w-100"/>
                                        <div className="col">
                                            <label htmlFor="toDate">Date</label>
                                        </div>
                                        <div className="col">
                                            {fmDate.isValid() && toDate.isValid() && (
                                                    <DatePicker
                                                            id="toDate"
                                                            className={`form-control form-control-sm ${inputClass}`}
                                                            dateFormat="yyyy/MM/dd"
                                                            minDate={dateForPickerDisplay(fmDate.toDate(), selectedTimezone) || undefined}
                                                            placeholderText="YYYY/MM/DD"
                                                            popperPlacement="top-end"
                                                            selected={dateForPickerDisplay(toDate.toDate(), selectedTimezone)}
                                                            onCalendarClose={() => setPeriodOption(PERIOD_CUSTOM)}
                                                            onChange={(date: Date) => setDateValue(date, 'to')}/>
                                            )}
                                        </div>
                                        <div className="w-100 py-1"/>
                                        <div className="col">
                                            <label htmlFor="toTime">Time</label>
                                        </div>
                                        <div className="col">
                                            <input type="text"
                                                   id="toTime"
                                                   className={`form-control form-control-sm ${inputClass}`}
                                                   maxLength={5}
                                                   placeholder="HH:MM"
                                                   value={toTime}
                                                   onBlur={validatePeriod}
                                                   onChange={(e) => {
                                                       setToTime(e.target.value);
                                                       updateAggregateOnPeriodChange()
                                                       setPeriodOption(PERIOD_CUSTOM)
                                                   }}/>
                                        </div>
                                    </div>
                                </div>
                                <div className="col mt-5 border-top">
                                    <div className="row g-0 heading">
                                        <div className="">
                                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                            Custom Interval
                                        </div>
                                    </div>

                                    <div className="p-3">
                                        <Dropdown
                                            ariaLabel="Select Aggregate"
                                            value={aggregate}
                                            onChange={(value) => setAggregate(Number(value))}
                                            options={[
                                                { value: 0, label: 'Auto' },
                                                ...AGGREGATE_OPTIONS.map(option => ({ ...option, disabled: aggregateDisabled(option.value) }))
                                            ]}
                                        />
                                    </div>
                                </div>
                                <div className="col p-2 text-end border-top">
                                    <button type="button" className="btn btn-secondary"
                                            onClick={() => closePeriodDropdown()}>Cancel
                                    </button>
                                    <button type="button" className="btn btn-primary ms-1" disabled={inputClass !== ''}
                                            onClick={setPeriod}>Apply
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* Predefined Periods */}
                        <div className="col-xs-12 col-lg-7">
                            <div className="row row-cols-xs-1 row-cols-sm-1 g-0 pb-lg-2">
                                <div className="col-12 ps-lg-2 heading d-lg-none">
                                    <i className="fal fa-history fa-fw" aria-hidden="true"/>
                                    Time Presets
                                </div>
                                <div className="col-xs-12 col-lg-6 pt-lg-2 px-2">
                                    <ul className="list-group list-group-flush">
                                        <li className={"list-group-item" + (periodOption === PERIOD_MINUTE ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_MINUTE)}>Last minute
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_FIVE_MINUTES ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_FIVE_MINUTES)}>Last 5
                                            minutes
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_TEN_MINUTES ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_TEN_MINUTES)}>Last 10
                                            minutes
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_THIRTY_MINUTES ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_THIRTY_MINUTES)}>Last 30
                                            minutes
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_ONE_HOUR ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_ONE_HOUR)}>Last hour
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_SIX_HOURS ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_SIX_HOURS)}>Last 6 hours
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_TWELVE_HOURS ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_TWELVE_HOURS)}>Last 12
                                            hours
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_TWENTY_FOUR_HOURS ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_TWENTY_FOUR_HOURS)}>Last 24
                                            hours
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_SEVEN_DAYS ? ' active' : '') + (periodDisabled(PERIOD_SEVEN_DAYS) ? ' disabled' : '')}
                                            role="button" aria-disabled={periodDisabled(PERIOD_SEVEN_DAYS)}
                                            onClick={() => { if (!periodDisabled(PERIOD_SEVEN_DAYS)) updatePeriod(PERIOD_SEVEN_DAYS); }}>Last 7 days
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_THIRTY_ONE_DAYS ? ' active' : '') + (periodDisabled(PERIOD_THIRTY_ONE_DAYS) ? ' disabled' : '')}
                                            role="button" aria-disabled={periodDisabled(PERIOD_THIRTY_ONE_DAYS)}
                                            onClick={() => { if (!periodDisabled(PERIOD_THIRTY_ONE_DAYS)) updatePeriod(PERIOD_THIRTY_ONE_DAYS); }}>Last 31
                                            days
                                        </li>
                                    </ul>
                                </div>
                                <div className="col-xs-12 col-lg-6 pt-lg-2 px-2">
                                    <ul className="list-group list-group-flush">
                                        <li className={"list-group-item border-top border-top-lg-none" + (periodOption === PERIOD_TODAY ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_TODAY)}>Today
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_YESTERDAY ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_YESTERDAY)}>Yesterday
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_DAY_BEFORE_YESTERDAY ? ' active' : '')}
                                            role="button" onClick={() => updatePeriod(PERIOD_DAY_BEFORE_YESTERDAY)}>Day
                                            Before Yesterday
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_THIS_WEEK ? ' active' : '') + (periodDisabled(PERIOD_THIS_WEEK) ? ' disabled' : '')}
                                            role="button" aria-disabled={periodDisabled(PERIOD_THIS_WEEK)}
                                            onClick={() => { if (!periodDisabled(PERIOD_THIS_WEEK)) updatePeriod(PERIOD_THIS_WEEK); }}>This Week
                                        </li>
                                        <li className={"list-group-item" + (periodOption === PERIOD_PREVIOUS_WEEK ? ' active' : '') + (periodDisabled(PERIOD_PREVIOUS_WEEK) ? ' disabled' : '')}
                                            role="button" aria-disabled={periodDisabled(PERIOD_PREVIOUS_WEEK)}
                                            onClick={() => { if (!periodDisabled(PERIOD_PREVIOUS_WEEK)) updatePeriod(PERIOD_PREVIOUS_WEEK); }}>Previous
                                            Week
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <ConditionalRender if={normalizeTz(Intl.DateTimeFormat().resolvedOptions().timeZone) !== 'UTC'}>
                            <div className="timezone-selector d-flex border-top align-items-center">
                                <label className='col text-end px-2'>Selected TimeZone: </label>
                                <div className="col">
                                    <Dropdown
                                        value={selectedTimezone}
                                        onChange={(value) => applyTimeZone(String(value))}
                                        options={[
                                            { value: 'UTC', label: 'UTC' },
                                            ...(normalizeTz(Intl.DateTimeFormat().resolvedOptions().timeZone) !== 'UTC'
                                                ? [{ value: normalizeTz(Intl.DateTimeFormat().resolvedOptions().timeZone), label: normalizeTz(Intl.DateTimeFormat().resolvedOptions().timeZone) }]
                                                : [])
                                        ]}
                                    />
                                </div>
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
            </React.Fragment>
    );
}

export default PeriodSelector;
